// PipelineBoardPage — Kanban dinamico do pipeline.
//
// Diferente do Kanban de tasks (/kanban) que agrupa por status: aqui as colunas
// sao as ETAPAS (steps) do pipeline carregadas dinamicamente da config canonica
// (pipeline_versions.config.steps[]). Os cards sao tasks com pipeline_step_id
// matching a etapa, agregadas de TODOS os pipeline_runs ATIVOS deste pipeline.
//
// Realtime: pipeline_runs (status changes) + tasks (movimento entre etapas).

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { TooltipProvider } from '@/components/ui/Tooltip';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  Workflow,
  Wrench,
  Layers,
  Globe,
  Building2,
  X,
  Eye,
  Filter,
} from 'lucide-react';
import { useSupabaseRealtime } from '@/hooks/useSupabaseRealtime';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';
import { getPipeline, type PipelineSummary, type PipelineVersionSummary } from '@/lib/api-pipelines';
import { runStatusLabel } from '@/lib/pipeline-counts';
import { demandStatusLabel } from '@/pages/mc/demand-constants';
import { PipelineRunDetailSheet } from '@/components/raios/PipelineRunDetailSheet';
// P1.13 F5 — TaskCard + TaskDetailSheet canonical (mesmos do MCTasksPage Kanban
// e DemandDetailPanel). Card visual = mesmo. Click → mesma sheet. Sem
// diferenciação por origem ou tipo de target. Princípio admin: "task é task,
// cara. É a mesma sheet."
import { TaskCard } from '@/components/raios/tasks/TaskCard';
import { TaskDetailSheet } from '@/components/raios/tasks/TaskDetailSheet';
import { backendToTaskUI, type TaskUI } from '@/components/raios/tasks/types';
import type { BackendTask } from '@/lib/api';

// P1.13 F5 — RunTask era shape skinny custom; agora alinhamos com BackendTask
// pra alimentar backendToTaskUI() e renderizar via TaskCard canonical.
type RunTask = BackendTask;

interface ActiveRun {
  id: string;
  demand_id: string;
  demand_code?: string | null;
  demand_title?: string | null;
  demand_status?: string | null;
  status: string;
  tasks_planned: number;
  tasks_done: number;
  tasks_failed: number;
  started_at: string | null;
}

interface ConfigStep {
  id: string;
  name?: string;
  kind?: string;
  target_slug?: string;
  handler_name?: string;
}

// Filtro do query: traz todos pipeline_runs nao-terminais pra board exibir.
// Categorizacao visual ("em curso" vs "pendentes" vs "bloqueados") e feita
// abaixo via filter local — ver runningRuns/pendingRuns/blockedRuns.
const NON_TERMINAL_RUN_STATUSES = ['pending', 'decomposing', 'executing', 'blocked'];

const KIND_META: Record<string, { icon: React.ComponentType<{ className?: string }>; tone: string }> = {
  agent: { icon: Workflow, tone: 'text-blue-600 dark:text-blue-400' },
  skill: { icon: Wrench, tone: 'text-purple-600 dark:text-purple-400' },
  task_orchestration: { icon: Layers, tone: 'text-amber-600 dark:text-amber-400' },
};

export function PipelineBoardPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // W2.3 — defesa em profundidade: filtra TODAS as queries supabase por
  // tenant_id (RLS já isola, mas filtro explícito evita vazamento se uma
  // policy regredir). tenant?.id é o tenant_id canonical (mesmo padrão de
  // useTasksBoard/mc-store/MCDemandsPage).
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? null;
  const [pipeline, setPipeline] = useState<(PipelineSummary & { versions: PipelineVersionSummary[] }) | null>(null);
  const [activeRuns, setActiveRuns] = useState<ActiveRun[]>([]);
  const [tasks, setTasks] = useState<RunTask[]>([]);
  // P1.13 F5 — selectedTaskId alimenta TaskDetailSheet canonical (mesmo
  // padrão do MCTasksPage e PipelineRunDetailSheet). Antes era objeto inline
  // que abria custom side-panel.
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [runSheetId, setRunSheetId] = useState<string | null>(null);
  // Selecao multi-run pra filtrar Kanban: vazio = mostra TODOS runs nao-terminais.
  // Algum selecionado = mostra SOMENTE tasks daqueles runs. Click no body do
  // chip toggla. Botao Eye no chip abre Sheet de detalhe da run individual.
  const [selectedRunIds, setSelectedRunIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reloadPipeline = useCallback(async () => {
    if (!id) return;
    try {
      const p = await getPipeline(id);
      setPipeline(p);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [id]);

  const reloadRuns = useCallback(async () => {
    if (!id) return;
    let runsQuery = supabase
      .from('pipeline_runs')
      .select('id, demand_id, status, tasks_planned, tasks_done, tasks_failed, started_at')
      .eq('pipeline_id', id)
      .in('status', NON_TERMINAL_RUN_STATUSES);
    if (tenantId) runsQuery = runsQuery.eq('tenant_id', tenantId);
    const { data, error: rErr } = await runsQuery.order('started_at', { ascending: false });
    if (rErr) {
      console.error('reloadRuns:', rErr.message);
      return;
    }
    const runs = ((data ?? []) as unknown) as ActiveRun[];
    // Hidrata cada run com info da demand (code/title/status) pra que os chips
    // mostrem CONTEXTO claro do que esta no run e nao soh UUID truncado.
    if (runs.length > 0) {
      const demandIds = Array.from(new Set(runs.map((r) => r.demand_id)));
      let demQuery = supabase
        .from('demands')
        .select('id, code, title, status')
        .in('id', demandIds);
      if (tenantId) demQuery = demQuery.eq('tenant_id', tenantId);
      const { data: dems } = await demQuery;
      const byId = new Map((dems ?? []).map((d) => [d.id as string, d]));
      for (const r of runs) {
        const d = byId.get(r.demand_id);
        if (d) {
          r.demand_code = d.code as string | null;
          r.demand_title = d.title as string | null;
          r.demand_status = d.status as string | null;
        }
      }
    }
    setActiveRuns(runs);
  }, [id, tenantId]);

  const reloadTasks = useCallback(async (runIds: string[]) => {
    if (runIds.length === 0) {
      setTasks([]);
      return;
    }
    // P1.13 F5 — select('*') pra TaskCard canonical (precisa shape rica
    // BackendTask completa pra alimentar backendToTaskUI). Mesmo padrão usado
    // em PipelineRunDetailSheet.reloadTasks.
    let tasksQuery = supabase
      .from('tasks')
      .select('*')
      .in('pipeline_run_id', runIds);
    if (tenantId) tasksQuery = tasksQuery.eq('tenant_id', tenantId);
    const { data, error: tErr } = await tasksQuery.order('created_at', { ascending: true });
    if (tErr) {
      console.error('reloadTasks:', tErr.message);
      return;
    }
    setTasks(((data ?? []) as unknown) as RunTask[]);
  }, [tenantId]);

  useEffect(() => {
    if (!id) return;
    let mounted = true;
    Promise.resolve().then(() => {
      if (!mounted) return;
      return Promise.all([reloadPipeline(), reloadRuns()])
        .catch((err: Error) => mounted && setError(err.message))
        .finally(() => mounted && setLoading(false));
    });
    return () => { mounted = false; };
  }, [id, reloadPipeline, reloadRuns]);

  const allActiveRunIds = useMemo(() => activeRuns.map((r) => r.id), [activeRuns]);

  // effectiveRunIds = runs selecionados pra filtrar tasks. Vazio = todos ativos.
  // Auto-poda IDs que nao existem mais (run completou e saiu do filtro).
  const effectiveRunIds = useMemo(() => {
    if (selectedRunIds.size === 0) return allActiveRunIds;
    const valid = allActiveRunIds.filter((rid) => selectedRunIds.has(rid));
    return valid.length > 0 ? valid : allActiveRunIds;
  }, [allActiveRunIds, selectedRunIds]);

  useEffect(() => {
    let cancelled = false;
    Promise.resolve().then(() => {
      if (!cancelled) void reloadTasks(effectiveRunIds);
    });
    return () => {
      cancelled = true;
    };
  }, [effectiveRunIds, reloadTasks]);

  // Auto-cleanup do Set quando run sai dos ativos
  useEffect(() => {
    if (selectedRunIds.size === 0) return;
    const validIds = new Set(allActiveRunIds);
    let dirty = false;
    const next = new Set<string>();
    for (const rid of selectedRunIds) {
      if (validIds.has(rid)) next.add(rid);
      else dirty = true;
    }
    if (!dirty) return;
    let cancelled = false;
    Promise.resolve().then(() => {
      if (!cancelled) setSelectedRunIds(next);
    });
    return () => {
      cancelled = true;
    };
  }, [allActiveRunIds, selectedRunIds]);

  // Realtime: pipeline_runs deste pipeline + tasks de runs ativos
  useSupabaseRealtime(
    { table: 'pipeline_runs', filter: id ? `pipeline_id=eq.${id}` : undefined },
    () => { reloadRuns(); },
  );
  useSupabaseRealtime(
    { table: 'tasks' },
    () => { reloadTasks(effectiveRunIds); },
  );

  const toggleRunSelected = (rid: string) => {
    setSelectedRunIds((prev) => {
      const next = new Set(prev);
      if (next.has(rid)) next.delete(rid);
      else next.add(rid);
      return next;
    });
  };
  const clearRunSelection = () => setSelectedRunIds(new Set());

  const latestVersion = useMemo(
    () => pipeline?.versions.find((v) => v.id === pipeline.latest_version_id),
    [pipeline],
  );

  const steps = useMemo<ConfigStep[]>(() => {
    if (!latestVersion) return [];
    return (latestVersion.config['steps'] as ConfigStep[] | undefined) ?? [];
  }, [latestVersion]);

  // tasksByStep: APENAS tasks nao-terminais (pending/queued/running) por etapa — popula a coluna do Kanban.
  // doneTasksList / failedTasksList: alimentam as colunas Concluidas e Falhas no fim do board.
  const NON_TERMINAL = new Set(['pending', 'queued', 'running']);

  const tasksByStep = useMemo(() => {
    const map = new Map<string, RunTask[]>();
    for (const s of steps) map.set(s.id, []);
    map.set('__unassigned__', []);
    for (const t of tasks) {
      if (!NON_TERMINAL.has(t.status)) continue; // done/failed/cancelled vao pras colunas dedicadas
      const k = t.pipeline_step_id ?? '__unassigned__';
      const list = map.get(k) ?? [];
      list.push(t);
      map.set(k, list);
    }
    return map;
  }, [steps, tasks]);

  const doneTasksList = useMemo(
    () => tasks.filter((t) => t.status === 'done' || t.status === 'cancelled'),
    [tasks],
  );
  const failedTasksList = useMemo(
    () => tasks.filter((t) => t.status === 'failed'),
    [tasks],
  );

  // P1.13 F5 — handler canonical pra abrir TaskDetailSheet ao click no card.
  // Recebe TaskUI (do TaskCard.onClick) e setta selectedTaskId.
  const handleTaskClick = useCallback((task: TaskUI) => {
    setSelectedTaskId(task.id);
  }, []);

  // P1.13 F5 — initialTask pra TaskDetailSheet renderizar header sem esperar
  // GET /tasks/:id voltar (mesmo padrão MCTasksPage).
  const selectedTaskInitial = useMemo<TaskUI | null>(() => {
    if (!selectedTaskId) return null;
    const t = tasks.find((x) => x.id === selectedTaskId);
    return t ? backendToTaskUI(t) : null;
  }, [selectedTaskId, tasks]);

  if (loading) return <Skeleton className="h-96 w-full" />;
  if (error) return <div className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 p-4 text-sm">{error}</div>;
  if (!pipeline) return null;

  // Runs em curso = SOMENTE executing/decomposing (algo rodando agora).
  // Pending/blocked NAO sao "em curso" — sao parados.
  const runningRunsCount = activeRuns.filter((r) => r.status === 'executing' || r.status === 'decomposing').length;
  const pendingRunsCount = activeRuns.filter((r) => r.status === 'pending').length;
  const blockedRunsCount = activeRuns.filter((r) => r.status === 'blocked').length;
  const totalTasks = tasks.length;
  const runningTasksCount = tasks.filter((t) => t.status === 'running').length;
  const pendingTasksCount = tasks.filter((t) => t.status === 'pending').length;
  // 'queued' não está no BackendTaskStatus canonico (apenas
  // pending/running/retry/done/failed/cancelled/paused). Cast defensivo
  // mantido caso backend exponha estado transient — soma 0 no shape atual.
  const queuedTasksCount = tasks.filter((t) => (t.status as string) === 'queued').length;
  const failedTasks = failedTasksList.length;
  const doneTasks = doneTasksList.length;

  return (
    /* P1.13 F5 — TooltipProvider envolve a página pra que tooltips do
        TaskCard canonical (timing, retry) renderizem corretamente. */
    <TooltipProvider delayDuration={200}>
    <div className="space-y-5">
      <PageHeader
        title={`Kanban — ${pipeline.name}`}
        description="Tasks dos runs não-terminais (em curso, pendentes, bloqueados) agrupadas pela etapa. Histórico de runs concluídos/falhados/cancelados não aparece aqui — clique numa run específica pra ver detalhe completo."
        actions={
          <Button size="sm" variant="outline" onClick={() => navigate('/pipelines')}>
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </Button>
        }
      />

      {/* Header status do pipeline */}
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={pipeline.scope_label === 'global' ? 'info' : 'outline'}>
          {pipeline.scope_label === 'global'
            ? <><Globe className="h-3 w-3 inline mr-1" /> Global</>
            : <><Building2 className="h-3 w-3 inline mr-1" /> Tenant</>}
        </Badge>
        {pipeline.latest_version_label && (
          <Badge variant="outline" className="font-mono">v{pipeline.latest_version_label}</Badge>
        )}
        {pipeline.is_active ? (
          <Badge variant="success">Ativo</Badge>
        ) : (
          <Badge variant="danger">Inativo</Badge>
        )}
        <code className="text-xs text-muted-foreground">{pipeline.slug}</code>
      </div>

      {/* Métricas top — separa RUNS (pipeline_runs) de TASKS (sub-tasks dentro
          de runs em curso). User: "em curso = rodando = tem tarefa sendo
          executada". Pending/blocked NAO contam como em curso. */}
      <div className="space-y-2">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Runs (execucoes do pipeline)</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-4">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Em curso</p>
            <p className="text-2xl font-mono mt-1 text-blue-600 dark:text-blue-400">{runningRunsCount}</p>
          </Card>
          <Card className="p-4">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Pendentes</p>
            <p className="text-2xl font-mono mt-1 text-muted-foreground">{pendingRunsCount}</p>
          </Card>
          <Card className="p-4">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Bloqueados</p>
            <p className="text-2xl font-mono mt-1 text-yellow-700 dark:text-yellow-400">{blockedRunsCount}</p>
          </Card>
          <Card className="p-4">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Total não-terminais</p>
            <p className="text-2xl font-mono mt-1">{runningRunsCount + pendingRunsCount + blockedRunsCount}</p>
          </Card>
        </div>
      </div>
      <div className="space-y-2">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Tasks (etapas dentro dos runs em curso)</p>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Card className="p-4">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Total tasks</p>
          <p className="text-2xl font-mono mt-1">{totalTasks}</p>
        </Card>
        <Card className="p-4">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Pendentes</p>
          <p className="text-2xl font-mono mt-1 text-muted-foreground">{pendingTasksCount}</p>
        </Card>
        <Card className="p-4">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Na fila</p>
          <p className="text-2xl font-mono mt-1 text-muted-foreground">{queuedTasksCount}</p>
        </Card>
        <Card className="p-4">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Em execução</p>
          <p className="text-2xl font-mono mt-1 text-blue-600 dark:text-blue-400">{runningTasksCount}</p>
        </Card>
        <Card className="p-4">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Concluídas</p>
          <p className="text-2xl font-mono mt-1 text-green-600 dark:text-green-400">{doneTasks}</p>
        </Card>
        <Card className="p-4">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Falhas</p>
          <p className="text-2xl font-mono mt-1 text-red-600 dark:text-red-400">{failedTasks}</p>
        </Card>
        </div>
      </div>

      {/* Runs nao-terminais separados por estado real:
          - "Em curso": executing/decomposing (algo rodando AGORA)
          - "Pendentes": pending (criado mas nao iniciou)
          - "Bloqueados": blocked (alternative paths esgotadas)
          Cada chip exibe demand code+title pra ficar claro QUE demand esta naquele run. */}
      {(() => {
        const runningRuns = activeRuns.filter((r) => r.status === 'executing' || r.status === 'decomposing');
        const pendingRuns = activeRuns.filter((r) => r.status === 'pending');
        const blockedRuns = activeRuns.filter((r) => r.status === 'blocked');
        const renderRunChip = (r: ActiveRun, accent: string, Icon: React.ComponentType<{ className?: string }>) => {
          const isSelected = selectedRunIds.has(r.id);
          const filterActive = selectedRunIds.size > 0;
          // Quando filtro ativo: chips selecionados ficam destacados;
          // nao-selecionados ficam dim ate hover.
          const chipClasses = isSelected
            ? 'border-brand bg-brand/10 ring-1 ring-brand/40'
            : filterActive
              ? 'border-raios-border bg-raios-elevated/30 opacity-50 hover:opacity-100'
              : 'border-raios-border bg-raios-elevated/30 hover:border-brand/40';
          return (
            <div
              key={r.id}
              className={`group inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs max-w-md transition ${chipClasses}`}
            >
              {/* Body do chip = toggle do filtro multi-select */}
              <button
                type="button"
                onClick={() => toggleRunSelected(r.id)}
                className="inline-flex items-center gap-2 min-w-0 cursor-pointer"
                title={isSelected ? 'Click pra remover esta run do filtro' : 'Click pra filtrar Kanban por essa run (multi-select)'}
              >
                <Icon className={`h-3 w-3 shrink-0 ${accent}`} />
                <span className="font-mono shrink-0">{r.demand_code ?? r.demand_id.slice(0,8)}</span>
                <span className="truncate">{r.demand_title ?? '(sem titulo)'}</span>
                {/* P1.13 F4: status canonical do pipeline_runs.status (a RUN). */}
                <Badge variant="outline" className="shrink-0" title={`pipeline_runs.status = ${r.status}`}>
                  run: {runStatusLabel(r.status)}
                </Badge>
                {/* Status da DEMANDA atrelada (demands.status) — distinto do
                    status da run. Hidratado em reloadRuns. */}
                {r.demand_status && (
                  <Badge variant="outline" className="shrink-0" title={`demands.status = ${r.demand_status}`}>
                    demanda: {demandStatusLabel(r.demand_status)}
                  </Badge>
                )}
                <span className="text-muted-foreground shrink-0">{r.tasks_done}/{r.tasks_planned}</span>
              </button>
              {/* Botao secundario = abre Sheet de detalhe da run individual */}
              <button
                type="button"
                onClick={() => setRunSheetId(r.id)}
                className="shrink-0 ml-1 p-1 rounded hover:bg-raios-elevated text-muted-foreground hover:text-foreground transition"
                title="Abrir detalhe completo da run"
                aria-label="Abrir detalhe da run"
              >
                <Eye className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        };
        const totalNonTerminal = runningRuns.length + pendingRuns.length + blockedRuns.length;
        return (
          <div className="space-y-3">
            {totalNonTerminal > 0 && (
              <p className="text-[11px] text-muted-foreground">
                Click no chip pra <span className="text-foreground font-medium">filtrar Kanban</span> por essa run (multi-select).
                Click no <Eye className="h-3 w-3 inline -mt-0.5" /> pra <span className="text-foreground font-medium">abrir detalhe completo</span> da run.
              </p>
            )}
            {runningRuns.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                  Em curso ({runningRuns.length})
                </h3>
                <div className="flex flex-wrap gap-2">
                  {runningRuns.map((r) => renderRunChip(r, 'text-blue-600 dark:text-blue-400', Clock))}
                </div>
              </div>
            )}
            {pendingRuns.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Pendentes ({pendingRuns.length})
                </h3>
                <div className="flex flex-wrap gap-2">
                  {pendingRuns.map((r) => renderRunChip(r, 'text-muted-foreground', AlertCircle))}
                </div>
              </div>
            )}
            {blockedRuns.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-yellow-700 dark:text-yellow-400">
                  Bloqueados ({blockedRuns.length})
                </h3>
                <div className="flex flex-wrap gap-2">
                  {blockedRuns.map((r) => renderRunChip(r, 'text-yellow-700 dark:text-yellow-400', AlertCircle))}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Indicador de filtro ativo: aparece quando 1+ runs selecionadas */}
      {selectedRunIds.size > 0 && (
        <div className="flex items-center gap-2 rounded-md border border-brand/40 bg-brand/10 px-3 py-2 text-xs">
          <Filter className="h-3.5 w-3.5 text-brand" />
          <span className="text-brand font-medium">
            Kanban filtrado por {selectedRunIds.size} run{selectedRunIds.size === 1 ? '' : 's'}
          </span>
          <span className="text-muted-foreground">
            (de {activeRuns.length} run{activeRuns.length === 1 ? '' : 's'} ativas)
          </span>
          <button
            type="button"
            onClick={clearRunSelection}
            className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded hover:bg-brand/20 text-brand transition"
          >
            <X className="h-3 w-3" />
            Limpar filtro
          </button>
        </div>
      )}

      {/* Kanban-por-etapa */}
      {steps.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-sm text-muted-foreground">Pipeline sem etapas configuradas.</p>
        </Card>
      ) : (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
            Kanban — {steps.length} {steps.length === 1 ? 'etapa' : 'etapas'}
            {selectedRunIds.size > 0 && (
              <span className="ml-2 text-brand normal-case font-normal">
                · filtrado por {selectedRunIds.size} run{selectedRunIds.size === 1 ? '' : 's'}
              </span>
            )}
          </h3>
          <div className="flex gap-3 overflow-x-auto pb-3">
            {steps.map((s) => {
              const stepTasks = tasksByStep.get(s.id) ?? [];
              const kindKey = s.kind ?? 'agent';
              const kindInfo = KIND_META[kindKey] ?? { icon: Workflow, tone: 'text-muted-foreground' };
              const KindIcon = kindInfo.icon;
              return (
                <div key={s.id} className="shrink-0 w-80 bg-raios-elevated/30 rounded-lg border border-raios-border flex flex-col max-h-[70vh]">
                  {/* Header da coluna — sem prefixo numerico (canonico ja esta no name).
                      Sem ícone de status agregado: agregar status de tasks de
                      várias runs/demandas numa etapa dava falsa sensação de
                      falha (1 task failed pintava a etapa inteira de vermelho). */}
                  <div className="px-3 py-2.5 border-b border-raios-border">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <h4 className="text-sm font-semibold truncate">{s.name ?? s.id}</h4>
                      </div>
                      <span className="text-xs font-mono text-muted-foreground tabular-nums shrink-0">
                        {stepTasks.length}
                      </span>
                    </div>
                    <div className={`flex items-center gap-1.5 text-[11px] ${kindInfo.tone}`}>
                      <KindIcon className="h-3 w-3" />
                      <span className="font-mono truncate">{s.target_slug ?? s.handler_name ?? kindKey}</span>
                    </div>
                  </div>

                  {/* Cards — apenas tasks nao-terminais (pending/queued/running).
                      P1.13 F5 — TaskCard canonical (mesmo do MCTasksPage Kanban
                      e DemandDetailPanel.TasksTab). Click → mesma TaskDetailSheet. */}
                  <div className="flex-1 overflow-y-auto p-2 space-y-2">
                    {stepTasks.length === 0 ? (
                      <p className="text-xs text-muted-foreground italic text-center py-4">sem tasks</p>
                    ) : (
                      stepTasks.map((t) => (
                        <TaskCard
                          key={t.id}
                          task={backendToTaskUI(t)}
                          onClick={handleTaskClick}
                        />
                      ))
                    )}
                  </div>
                </div>
              );
            })}

            {/* Coluna unassigned (orphan tasks ativas sem step) */}
            {(tasksByStep.get('__unassigned__') ?? []).length > 0 && (
              <div className="shrink-0 w-80 bg-yellow-100/30 dark:bg-yellow-900/10 rounded-lg border border-yellow-300/50 flex flex-col max-h-[70vh]">
                <div className="px-3 py-2.5 border-b border-yellow-300/50">
                  <h4 className="text-sm font-semibold flex items-center gap-1.5">
                    <AlertCircle className="h-3.5 w-3.5 text-yellow-600 dark:text-yellow-400" />
                    Sem etapa
                  </h4>
                  <p className="text-[11px] text-muted-foreground mt-1">Tasks sem pipeline_step_id (orphan)</p>
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-2">
                  {/* P1.13 F5 — TaskCard canonical pra unassigned também. */}
                  {(tasksByStep.get('__unassigned__') ?? []).map((t) => (
                    <TaskCard
                      key={t.id}
                      task={backendToTaskUI(t)}
                      onClick={handleTaskClick}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Coluna Concluidas — tasks terminadas com sucesso (done + cancelled) */}
            <div className="shrink-0 w-80 bg-green-100/20 dark:bg-green-900/10 rounded-lg border border-green-300/50 flex flex-col max-h-[70vh]">
              <div className="px-3 py-2.5 border-b border-green-300/50">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-600 dark:text-green-400 shrink-0" />
                    <h4 className="text-sm font-semibold truncate">Concluídas</h4>
                  </div>
                  <span className="text-xs font-mono text-muted-foreground tabular-nums shrink-0">
                    {doneTasksList.length}
                  </span>
                </div>
                <p className="text-[11px] text-muted-foreground">Tasks que terminaram com sucesso</p>
              </div>
              <div className="flex-1 overflow-y-auto p-2 space-y-2">
                {doneTasksList.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic text-center py-4">nenhuma ainda</p>
                ) : (
                  /* P1.13 F5 — TaskCard canonical pra coluna Concluídas. */
                  doneTasksList.map((t) => (
                    <TaskCard
                      key={t.id}
                      task={backendToTaskUI(t)}
                      onClick={handleTaskClick}
                    />
                  ))
                )}
              </div>
            </div>

            {/* Coluna Falhas — tasks com status=failed */}
            <div className="shrink-0 w-80 bg-red-100/20 dark:bg-red-900/10 rounded-lg border border-red-300/50 flex flex-col max-h-[70vh]">
              <div className="px-3 py-2.5 border-b border-red-300/50">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <XCircle className="h-3.5 w-3.5 text-red-600 dark:text-red-400 shrink-0" />
                    <h4 className="text-sm font-semibold truncate">Falhas</h4>
                  </div>
                  <span className="text-xs font-mono text-muted-foreground tabular-nums shrink-0">
                    {failedTasksList.length}
                  </span>
                </div>
                <p className="text-[11px] text-muted-foreground">Tasks que falharam (status=failed)</p>
              </div>
              <div className="flex-1 overflow-y-auto p-2 space-y-2">
                {failedTasksList.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic text-center py-4">nenhuma ainda</p>
                ) : (
                  /* P1.13 F5 — TaskCard canonical pra coluna Falhas. */
                  failedTasksList.map((t) => (
                    <TaskCard
                      key={t.id}
                      task={backendToTaskUI(t)}
                      onClick={handleTaskClick}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* P1.13 F5 — TaskDetailSheet canonical (mesma sheet do MCTasksPage
          Kanban geral e DemandDetailPanel.TasksTab). Substitui custom side
          panel "nada a ver" reportado pelo admin. Princípio canonical: task
          é task, mesma sheet sempre. */}
      <TaskDetailSheet
        taskId={selectedTaskId}
        initialTask={selectedTaskInitial}
        onClose={() => setSelectedTaskId(null)}
      />

      {/* Sheet rica de detalhe da run — abre ao clicar num chip de run */}
      <PipelineRunDetailSheet
        runId={runSheetId}
        open={runSheetId !== null}
        onClose={() => setRunSheetId(null)}
      />
    </div>
    </TooltipProvider>
  );
}
